<template>
	<AppDialog
		disableMaxWidth
		:closeRight="'-10'"
		:closeTop="'-30'"
		dialogName="TestURLTool"
		width="620"
		@onCancel="cancel"
		contentClass="px-9"
	>
		<div class="title pt-6">{{ title }}</div>
		<div class="dialog-content rounded pb-4 pt-6">
			<template v-if="hasParams">
				<div class="pb-1">
					<h2 class="pb-1">Sample parameters</h2>
					<h3>{{ parametersDescription }}</h3>
				</div>
				<URLToolTriggerParams v-model="testParams" />
			</template>
			<div class="pt-1" v-if="!testTriggerStepMode || hasParams">
				<h2>URL to be tested</h2>
				<v-text-field class="pt-1" disabled :value="testURL" outlined dense />
				<div class="flex-centerize">
					<h2 class="note" v-if="!testTriggerStepMode">Note: actions performed use real data, just like a regular feature run.</h2>
				</div>
			</div>
			<div v-if="testTriggerStepMode && isCustomerAuthOn">
				<h2 class="pb-1">Sample verified customer email</h2>
				<h3 class="sub-title my-1">
					No email will be sent when testing. The sample email can be mapped from “Runtime & user attributes” into later workflow steps.
				</h3>
				<SampleCustomerEmail class="mt-3" />
			</div>
			<div class="flex-space-around--center pt-4 pb-4">
				<v-btn
					pressed
					dark
					class="btn btn-lg btn-primary"
					@click="test"
					:style="{
						'border-radius': '10px !important',
					}"
				>
					<template v-if="testTriggerStepMode"> Test step </template>
					<template v-else> Begin test run <AppIcon class="icon-link pl-1" name="external-link" /> </template>
				</v-btn>
			</div>
		</div>
	</AppDialog>
</template>
<script>
import URLToolTriggerParams from './URLToolTriggerParams.vue';
import SampleCustomerEmail from '@/components/BlockEditorToolWorkflow/SampleCustomerEmail.vue';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { getNode } from '@/store/modules/nodes/getters';
import { isCustomerAuthOn } from '@/store/modules/selectedWorkflow/getters';
import { getWorkflow } from '@/store/modules/workflows/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import WorkflowService from '@/utils/WorkflowService';
import { TEST_MODE_QUERY_PARAM } from '@/views/FlowRun/flow-run.const';
export default {
	name: 'TestURLToolDialog',
	components: { AppDialog, SampleCustomerEmail, URLToolTriggerParams },
	props: {
		workflowId: { type: Number, required: true },
		mode: { type: String, validator: (value) => ['feature', 'trigger-step'].includes(value) },
		dialogPromiseHandlers: { type: Object },
	},
	computed: {
		...mapGettersShorthand({ getWorkflow, getNode, isCustomerAuthOn }),
		testTriggerStepMode() {
			return this.mode === 'trigger-step';
		},
		parametersDescription() {
			return this.testTriggerStepMode
				? `Enter parameters you wish to use in the test. You'll be able to use these parameters as sample data as you build your feature.`
				: 'Enter parameters you wish to use in the test run.';
		},
		title() {
			return this.mode === 'feature' ? 'Test run your feature' : 'Enter sample data to test this step';
		},
		originalTestValues() {
			const node = this.getNode(this.workflow.current_draft.root_node);
			return Object.fromEntries(
				Object.keys(node.params_map.url_parameters || {}).map((key) => [key, node.test_result.url_parameters?.[key] || 'xxxx'])
			);
		},
		workflow() {
			return this.getWorkflow(this.workflowId);
		},
		hasParams() {
			return Object.values(this.testParams).length > 0;
		},
		testURL() {
			return WorkflowService.buildTriggerURL(this.workflow.run_link, this.testParams).href;
		},
	},
	methods: {
		...mapActionsShorthand({}),
		test() {
			if (!this.testTriggerStepMode) {
				const testURL = new URL(this.testURL);
				testURL.searchParams.set(TEST_MODE_QUERY_PARAM, '1');
				window.open(testURL.href, '_blank');
			}
			this.dialogPromiseHandlers.resolve(this.testParams);
			AppDialogManagerService.closeCurrentDialog();
		},
		cancel() {
			this.dialogPromiseHandlers.reject('cancel');
		},
	},
	mounted() {
		this.testParams = { ...this.originalTestValues };
	},
	data() {
		return { testParams: {} };
	},
};
</script>

<style scoped lang="scss">
.dialog-content {
	background: var(--white);
}
.btn {
	background-color: var(--app-purple) !important;
	height: 40px !important;
}
h3,
.note {
	color: #646e87;
	line-height: 1.1rem;
}
.icon-link {
	::v-deep {
		path {
			stroke: #fff;
		}
	}
}
</style>
